import { useCallback, useEffect, useState } from 'react';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import { MagicJobStatus } from '@/types/gql.generated';
import { useChatContext } from '../../contexts/ChatContext/useChatContext';
import { useMagic } from '../../contexts/MagicContext';
import { useCreateChat } from '../../hooks/useCreateChat';
import type { DecoratedMagicJob } from '../../types';
import { createDecoratedMagicChat } from '../../utils/createDecoratedMagicChat';
import { AuthModal } from './AuthModal';

const RUNNING_STATUSES: MagicJobStatus[] = [
  MagicJobStatus.Waiting,
  MagicJobStatus.Active,
];

const isJobRunning = (job: DecoratedMagicJob): boolean =>
  !!job.status && RUNNING_STATUSES.includes(job.status);

export const AuthModalLoader = () => {
  const { resetAppState } = useMagic();
  const { setChat } = useChatContext();
  const { createChat } = useCreateChat();

  // Signing in after an anonymous job converts the job into a chat. The server
  // assigns the chat its own id, so the result has to land in the app's chat
  // context (mounted by Bootstrap, which this modal renders inside) — that's
  // the id every subsequent chat request is keyed on. Surfaces that mount the
  // modal without a chat context (the extension, the share page) have no chat
  // to switch to; the conversion is idempotent server-side, so dropping the
  // result there is harmless.
  const convertJob = useCallback(
    (jobId: DecoratedMagicJob['id']) => {
      createChat(
        {
          from: { magicJobId: jobId },
        },
        {
          onSuccess: (data) => {
            const chat = createDecoratedMagicChat(data.createChat);
            setChat(chat);
          },
        }
      );
    },
    [createChat, setChat]
  );

  // The chat is a one-time copy of the job's events, so the server refuses to
  // make one while the job is still running. Signing in mid-run waits for the
  // job on the page to finish, then converts it.
  const { base } = useCollection();
  const liveJob = base?.job;
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingJobId || !liveJob) {
      return;
    }
    if (liveJob.id !== pendingJobId) {
      // the page moved on to another job
      setPendingJobId(null);
    } else if (!isJobRunning(liveJob)) {
      setPendingJobId(null);
      convertJob(pendingJobId);
    }
  }, [pendingJobId, liveJob, convertJob]);

  const handleCreateChat = (job: DecoratedMagicJob) => {
    if (job.id && isJobRunning(job)) {
      setPendingJobId(job.id);
    } else {
      convertJob(job.id);
    }
  };

  return (
    <AuthModal
      onChatCreate={handleCreateChat}
      onResetAppState={resetAppState}
    />
  );
};
